import { type Dispatch, type SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useUpdateFeature } from '@/features/features';
import { entityFormAdapters } from '@/features/forms';
import { useReleasesQuery } from '@/features/releases';
import { useSuitesQuery, useUpdateSuite } from '@/features/suites';
import { useTestsQuery, useUpdateTest } from '@/features/tests';
import { queryTests } from '@/features/tests/data';
import { useDialogToggle, useInvalidateQueries, useResolvedFeatureFlags } from '@/shared/hooks';
import { useInvalidateGrids } from '@/shared/hooks/useInvalidateGrids';
import { useProjectContextGuard } from '@/shared/hooks/useProjectContextGuard';
import { buildEqFilter, mergeFilters } from '@/ui/composites/grid/gridFilters';
import {
  useDialogFormState,
  type DialogFormStateWithHandlers,
} from '@/ui/hooks/useDialogFormState';
import { useFormErrors, type FieldErrorProps } from '@/ui/hooks/useFormErrors';
import { useLinkSelectDialogState } from '@/ui/hooks/useLinkSelectDialogState';
import { useFeatureDetailQuery, useDeleteFeature } from './features';
import type { FeatureFormValues } from '@/features/forms';
import type { GridPath } from '@/shared/hooks';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  FeatureDto,
  QueryResult,
  ReleaseListItemDto,
  SuiteListItemDto,
  TestListItemDto,
} from '@flowqa/shared';
import type { QueryFilters } from '@tanstack/react-query';

type FeatureDetailViewModelParams = {
  slug: string | undefined;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  setSelectedProjectId: (value: string | null) => void;
};

type FeatureDetailViewModel = {
  searchSlug: string;
  featureId: string;
  featureProjectId: string;
  featureQuery: ReturnType<typeof useFeatureDetailQuery>;
  feature: FeatureDto | null | undefined;
  suitesQuery: ReturnType<typeof useSuitesQuery>;
  testsQuery: ReturnType<typeof useTestsQuery>;
  suiteOptions: SuiteListItemDto[];
  testOptions: TestListItemDto[];
  releasesQuery: ReturnType<typeof useReleasesQuery>;
  releaseOptions: ReleaseListItemDto[];
  releaseOptionIds: Set<string>;
  editForm: DialogFormStateWithHandlers<FeatureFormValues>;
  editErrors: {
    errorFor: (key: keyof FeatureFormValues) => string | undefined;
    fieldErrorProps: (key: keyof FeatureFormValues) => FieldErrorProps;
    clearError: (key: keyof FeatureFormValues) => void;
  };
  editValues: FeatureFormValues;
  linkSuiteDialog: ReturnType<typeof useDialogToggle>;
  unlinkSuiteDialog: ReturnType<typeof useDialogToggle>;
  linkTestDialog: ReturnType<typeof useLinkSelectDialogState>;
  unlinkTestDialog: ReturnType<typeof useLinkSelectDialogState>;
  linkTestForm: ReturnType<typeof useLinkSelectDialogState>['form'];
  linkTestErrors: ReturnType<typeof useLinkSelectDialogState>['errors'];
  unlinkTestForm: ReturnType<typeof useLinkSelectDialogState>['form'];
  unlinkTestErrors: ReturnType<typeof useLinkSelectDialogState>['errors'];
  editOpen: boolean;
  setEditOpen: (open: boolean) => void;
  deleteOpen: boolean;
  setDeleteOpen: (open: boolean) => void;
  linkSuiteId: string;
  setLinkSuiteId: (value: string) => void;
  linkSuiteSelectedTestIds: string[];
  setLinkSuiteSelectedTestIds: Dispatch<SetStateAction<string[]>>;
  linkSuiteSaving: boolean;
  setLinkSuiteSaving: (value: boolean) => void;
  linkSuiteFieldError: string | null;
  setLinkSuiteFieldError: (value: string | null) => void;
  linkSuiteTestsLoading: boolean;
  unlinkSuiteId: string;
  setUnlinkSuiteId: (value: string) => void;
  unlinkSuiteSelectedTestIds: string[];
  setUnlinkSuiteSelectedTestIds: Dispatch<SetStateAction<string[]>>;
  unlinkSuiteSaving: boolean;
  setUnlinkSuiteSaving: (value: boolean) => void;
  unlinkSuiteFieldError: string | null;
  setUnlinkSuiteFieldError: (value: string | null) => void;
  unlinkSuiteTestsLoading: boolean;
  linkSuiteTestOptions: Array<{ id: string; name: string }>;
  unlinkSuiteTestOptions: Array<{ id: string; name: string }>;
  linkSuiteTests: TestListItemDto[];
  unlinkSuiteTests: TestListItemDto[];
  availableTestOptions: TestListItemDto[];
  unlinkTestOptions: TestListItemDto[];
  availableSuiteOptions: SuiteListItemDto[];
  linkedSuites: SuiteListItemDto[];
  suiteFilters: ReturnType<typeof buildEqFilter>;
  testQueryFn: (input: GridQueryInput) => Promise<QueryResult<TestListItemDto>>;
  updateFeature: ReturnType<typeof useUpdateFeature>;
  deleteFeature: ReturnType<typeof useDeleteFeature>;
  updateSuite: ReturnType<typeof useUpdateSuite>;
  updateTest: ReturnType<typeof useUpdateTest>;
  invalidateGrids: (paths: GridPath[]) => Promise<void>;
  invalidateQueries: (filters: QueryFilters[]) => Promise<void>;
};

type FeatureDetailDataState = {
  searchSlug: string;
  featureQuery: ReturnType<typeof useFeatureDetailQuery>;
  feature: FeatureDto | null | undefined;
  suitesQuery: ReturnType<typeof useSuitesQuery>;
  testsQuery: ReturnType<typeof useTestsQuery>;
  suiteOptions: SuiteListItemDto[];
  testOptions: TestListItemDto[];
  releasesQuery: ReturnType<typeof useReleasesQuery>;
  releaseOptions: ReleaseListItemDto[];
  releaseOptionIds: Set<string>;
  featureId: string;
  featureProjectId: string;
  linkedSuites: SuiteListItemDto[];
  availableSuiteOptions: SuiteListItemDto[];
};

type FeatureEditFormState = {
  editForm: DialogFormStateWithHandlers<FeatureFormValues>;
  editErrors: {
    errorFor: (key: keyof FeatureFormValues) => string | undefined;
    fieldErrorProps: (key: keyof FeatureFormValues) => FieldErrorProps;
    clearError: (key: keyof FeatureFormValues) => void;
  };
  editValues: FeatureFormValues;
};

type FeatureSuiteLinkingState = {
  linkSuiteId: string;
  setLinkSuiteId: (value: string) => void;
  linkSuiteSelectedTestIds: string[];
  setLinkSuiteSelectedTestIds: Dispatch<SetStateAction<string[]>>;
  linkSuiteSaving: boolean;
  setLinkSuiteSaving: (value: boolean) => void;
  linkSuiteFieldError: string | null;
  setLinkSuiteFieldError: (value: string | null) => void;
  linkSuiteTestsLoading: boolean;
  unlinkSuiteId: string;
  setUnlinkSuiteId: (value: string) => void;
  unlinkSuiteSelectedTestIds: string[];
  setUnlinkSuiteSelectedTestIds: Dispatch<SetStateAction<string[]>>;
  unlinkSuiteSaving: boolean;
  setUnlinkSuiteSaving: (value: boolean) => void;
  unlinkSuiteFieldError: string | null;
  setUnlinkSuiteFieldError: (value: string | null) => void;
  unlinkSuiteTestsLoading: boolean;
  linkSuiteTests: TestListItemDto[];
  unlinkSuiteTests: TestListItemDto[];
  linkSuiteTestOptions: Array<{ id: string; name: string }>;
  unlinkSuiteTestOptions: Array<{ id: string; name: string }>;
};

type FeatureTestLinkingState = {
  linkTestDialog: ReturnType<typeof useLinkSelectDialogState>;
  unlinkTestDialog: ReturnType<typeof useLinkSelectDialogState>;
  linkTestForm: ReturnType<typeof useLinkSelectDialogState>['form'];
  linkTestErrors: ReturnType<typeof useLinkSelectDialogState>['errors'];
  unlinkTestForm: ReturnType<typeof useLinkSelectDialogState>['form'];
  unlinkTestErrors: ReturnType<typeof useLinkSelectDialogState>['errors'];
  availableTestOptions: TestListItemDto[];
  unlinkTestOptions: TestListItemDto[];
};

const useFeatureDetailData = ({
  slug,
  selectedOrganisationId: _selectedOrganisationId,
  selectedProjectId,
  setSelectedProjectId,
}: FeatureDetailViewModelParams): FeatureDetailDataState => {
  const searchSlug = useMemo(() => (slug ?? '').trim(), [slug]);
  const featureQuery = useFeatureDetailQuery(searchSlug || null, selectedProjectId ?? null);
  const feature = featureQuery.data;

  const suitesQuery = useSuitesQuery(feature?.projectId ?? null, {
    enabled: Boolean(feature?.projectId),
  });
  const testsQuery = useTestsQuery(feature?.projectId ?? null, {
    enabled: Boolean(feature?.projectId),
  });
  const suiteOptions = suitesQuery.data?.items ?? [];
  const testOptions = testsQuery.data?.items ?? [];

  useProjectContextGuard({
    entity: feature,
    isPending: featureQuery.isPending,
    isFetched: featureQuery.isFetched,
    selectedProjectId,
    listPath: '/features',
    setSelectedProjectId,
    syncContextOnMismatch: true,
  });

  const releaseProjectId = feature?.projectId ?? null;
  const releasesQuery = useReleasesQuery(releaseProjectId, { enabled: Boolean(releaseProjectId) });
  const releaseOptions = releasesQuery.data?.items ?? [];
  const releaseOptionIds = useMemo(
    () => new Set(releaseOptions.map((release) => release.id)),
    [releaseOptions],
  );

  const featureId = feature?.id ?? '';
  const featureProjectId = feature?.projectId ?? '';
  const linkedSuites = useMemo(
    () =>
      suiteOptions.filter((suite) => {
        const featureIds = suite.features?.ids;
        return featureId.length > 0 && Array.isArray(featureIds) && featureIds.includes(featureId);
      }),
    [featureId, suiteOptions],
  );
  const availableSuiteOptions = useMemo(
    () => suiteOptions.filter((suite) => !linkedSuites.some((linked) => linked.id === suite.id)),
    [linkedSuites, suiteOptions],
  );

  return {
    searchSlug,
    featureQuery,
    feature,
    suitesQuery,
    testsQuery,
    suiteOptions,
    testOptions,
    releasesQuery,
    releaseOptions,
    releaseOptionIds,
    featureId,
    featureProjectId,
    linkedSuites,
    availableSuiteOptions,
  };
};

const useFeatureEditForm = ({
  feature,
  releasesQuery,
  releaseOptionIds,
}: {
  feature: FeatureDto | null | undefined;
  releasesQuery: ReturnType<typeof useReleasesQuery>;
  releaseOptionIds: Set<string>;
}): FeatureEditFormState => {
  const editDefaults = useMemo(
    () => entityFormAdapters.feature.toFormValues(feature),
    [
      feature?.id,
      feature?.name,
      feature?.status,
      feature?.projectId,
      feature?.releaseId,
      feature?.plannedFor,
      feature?.releasedAt,
    ],
  );
  const editForm = useDialogFormState(editDefaults);
  const editErrors = useFormErrors(editForm);
  const { setField: setEditField, values: editValues } = editForm;

  const lastFeatureIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!feature) return;
    if (lastFeatureIdRef.current === feature.id) return;
    lastFeatureIdRef.current = feature.id;
    setEditField('name', editDefaults.name);
    setEditField('status', editDefaults.status);
    setEditField('releaseId', editDefaults.releaseId);
    setEditField('plannedFor', editDefaults.plannedFor);
    setEditField('releasedAt', editDefaults.releasedAt);
  }, [editDefaults, feature, setEditField]);

  useEffect(() => {
    if (!releasesQuery.isFetched) return;
    const releaseId = editValues.releaseId;
    if (!releaseId) return;
    if (!releaseOptionIds.has(releaseId)) {
      setEditField('releaseId', '');
    }
  }, [editValues.releaseId, releaseOptionIds, releasesQuery.isFetched, setEditField]);

  return {
    editForm,
    editErrors,
    editValues,
  };
};

const useFeatureSuiteLinkingState = ({
  linkSuiteDialogOpen,
  unlinkSuiteDialogOpen,
  featureProjectId,
  selectedOrganisationId,
}: {
  linkSuiteDialogOpen: boolean;
  unlinkSuiteDialogOpen: boolean;
  featureProjectId: string;
  selectedOrganisationId: string | null;
}): FeatureSuiteLinkingState => {
  const [linkSuiteId, setLinkSuiteId] = useState('');
  const [linkSuiteTests, setLinkSuiteTests] = useState<TestListItemDto[]>([]);
  const [linkSuiteSelectedTestIds, setLinkSuiteSelectedTestIds] = useState<string[]>([]);
  const [linkSuiteSaving, setLinkSuiteSaving] = useState(false);
  const [linkSuiteFieldError, setLinkSuiteFieldError] = useState<string | null>(null);
  const [linkSuiteTestsLoading, setLinkSuiteTestsLoading] = useState(false);
  const [unlinkSuiteId, setUnlinkSuiteId] = useState('');
  const [unlinkSuiteTests, setUnlinkSuiteTests] = useState<TestListItemDto[]>([]);
  const [unlinkSuiteSelectedTestIds, setUnlinkSuiteSelectedTestIds] = useState<string[]>([]);
  const [unlinkSuiteSaving, setUnlinkSuiteSaving] = useState(false);
  const [unlinkSuiteFieldError, setUnlinkSuiteFieldError] = useState<string | null>(null);
  const [unlinkSuiteTestsLoading, setUnlinkSuiteTestsLoading] = useState(false);

  useEffect(() => {
    if (!linkSuiteDialogOpen) {
      setLinkSuiteId('');
      setLinkSuiteTests([]);
      setLinkSuiteSelectedTestIds([]);
      setLinkSuiteFieldError(null);
      setLinkSuiteTestsLoading(false);
    }
  }, [linkSuiteDialogOpen]);

  useEffect(() => {
    if (!unlinkSuiteDialogOpen) {
      setUnlinkSuiteId('');
      setUnlinkSuiteTests([]);
      setUnlinkSuiteSelectedTestIds([]);
      setUnlinkSuiteFieldError(null);
      setUnlinkSuiteTestsLoading(false);
    }
  }, [unlinkSuiteDialogOpen]);

  useEffect(() => {
    if (!linkSuiteDialogOpen || linkSuiteId.length === 0 || featureProjectId.length === 0) {
      return;
    }
    let active = true;
    const fetchSuiteTests = async (): Promise<void> => {
      setLinkSuiteTestsLoading(true);
      try {
        const result = (await queryTests(featureProjectId, selectedOrganisationId ?? undefined, {
          page: 1,
          pageSize: 100,
          sorts: [],
          filters: buildEqFilter('suiteId', linkSuiteId),
        })) as QueryResult<TestListItemDto>;
        if (!active) return;
        const items = result.items;
        setLinkSuiteTests(items);
        setLinkSuiteSelectedTestIds(items.map((item) => item.id));
      } catch {
        if (!active) return;
        setLinkSuiteTests([]);
        setLinkSuiteSelectedTestIds([]);
      } finally {
        if (active) {
          setLinkSuiteTestsLoading(false);
        }
      }
    };
    void fetchSuiteTests();
    return () => {
      active = false;
    };
  }, [featureProjectId, linkSuiteDialogOpen, linkSuiteId, selectedOrganisationId]);

  useEffect(() => {
    if (!unlinkSuiteDialogOpen || unlinkSuiteId.length === 0 || featureProjectId.length === 0) {
      return;
    }
    let active = true;
    const fetchSuiteTests = async (): Promise<void> => {
      setUnlinkSuiteTestsLoading(true);
      try {
        const result = (await queryTests(featureProjectId, selectedOrganisationId ?? undefined, {
          page: 1,
          pageSize: 100,
          sorts: [],
          filters: buildEqFilter('suiteId', unlinkSuiteId),
        })) as QueryResult<TestListItemDto>;
        if (!active) return;
        const items = result.items;
        setUnlinkSuiteTests(items);
        setUnlinkSuiteSelectedTestIds(items.map((item) => item.id));
      } catch {
        if (!active) return;
        setUnlinkSuiteTests([]);
        setUnlinkSuiteSelectedTestIds([]);
      } finally {
        if (active) {
          setUnlinkSuiteTestsLoading(false);
        }
      }
    };
    void fetchSuiteTests();
    return () => {
      active = false;
    };
  }, [featureProjectId, selectedOrganisationId, unlinkSuiteDialogOpen, unlinkSuiteId]);

  const linkSuiteTestOptions = useMemo(
    () => linkSuiteTests.map((test) => ({ id: test.id, name: test.name })),
    [linkSuiteTests],
  );
  const unlinkSuiteTestOptions = useMemo(
    () => unlinkSuiteTests.map((test) => ({ id: test.id, name: test.name })),
    [unlinkSuiteTests],
  );

  return {
    linkSuiteId,
    setLinkSuiteId,
    linkSuiteSelectedTestIds,
    setLinkSuiteSelectedTestIds,
    linkSuiteSaving,
    setLinkSuiteSaving,
    linkSuiteFieldError,
    setLinkSuiteFieldError,
    linkSuiteTestsLoading,
    unlinkSuiteId,
    setUnlinkSuiteId,
    unlinkSuiteSelectedTestIds,
    setUnlinkSuiteSelectedTestIds,
    unlinkSuiteSaving,
    setUnlinkSuiteSaving,
    unlinkSuiteFieldError,
    setUnlinkSuiteFieldError,
    unlinkSuiteTestsLoading,
    linkSuiteTests,
    unlinkSuiteTests,
    linkSuiteTestOptions,
    unlinkSuiteTestOptions,
  };
};

const useFeatureTestLinkingState = ({
  featureId,
  testOptions,
}: {
  featureId: string;
  testOptions: TestListItemDto[];
}): FeatureTestLinkingState => {
  const linkTestDialog = useLinkSelectDialogState();
  const unlinkTestDialog = useLinkSelectDialogState();
  const linkTestForm = linkTestDialog.form;
  const linkTestErrors = linkTestDialog.errors;
  const unlinkTestForm = unlinkTestDialog.form;
  const unlinkTestErrors = unlinkTestDialog.errors;

  const directTestIds = useMemo(
    () =>
      testOptions
        .filter((test) => (test.features?.ids ?? []).includes(featureId))
        .map((test) => test.id),
    [featureId, testOptions],
  );
  const directTestIdSet = useMemo(() => new Set(directTestIds), [directTestIds]);
  const availableTestOptions = useMemo(
    () => testOptions.filter((test) => !directTestIdSet.has(test.id)),
    [directTestIdSet, testOptions],
  );
  const unlinkTestOptions = useMemo(
    () => testOptions.filter((test) => directTestIdSet.has(test.id)),
    [directTestIdSet, testOptions],
  );

  return {
    linkTestDialog,
    unlinkTestDialog,
    linkTestForm,
    linkTestErrors,
    unlinkTestForm,
    unlinkTestErrors,
    availableTestOptions,
    unlinkTestOptions,
  };
};

export const useFeatureDetailViewModel = ({
  slug,
  selectedOrganisationId,
  selectedProjectId,
  setSelectedProjectId,
}: FeatureDetailViewModelParams): FeatureDetailViewModel => {
  const detailData = useFeatureDetailData({
    slug,
    selectedOrganisationId,
    selectedProjectId,
    setSelectedProjectId,
  });
  const {
    searchSlug,
    featureQuery,
    feature,
    suitesQuery,
    testsQuery,
    suiteOptions,
    testOptions,
    releasesQuery,
    releaseOptions,
    releaseOptionIds,
    featureId,
    featureProjectId,
    linkedSuites,
    availableSuiteOptions,
  } = detailData;

  const editState = useFeatureEditForm({
    feature,
    releasesQuery,
    releaseOptionIds,
  });
  const { editForm, editErrors, editValues } = editState;

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const updateFeature = useUpdateFeature();
  const deleteFeature = useDeleteFeature();
  const updateSuite = useUpdateSuite();
  const updateTest = useUpdateTest();
  const linkSuiteDialog = useDialogToggle();
  const unlinkSuiteDialog = useDialogToggle();

  const suiteLinkingState = useFeatureSuiteLinkingState({
    linkSuiteDialogOpen: linkSuiteDialog.open,
    unlinkSuiteDialogOpen: unlinkSuiteDialog.open,
    featureProjectId,
    selectedOrganisationId,
  });
  const {
    linkSuiteId,
    setLinkSuiteId,
    linkSuiteSelectedTestIds,
    setLinkSuiteSelectedTestIds,
    linkSuiteSaving,
    setLinkSuiteSaving,
    linkSuiteFieldError,
    setLinkSuiteFieldError,
    linkSuiteTestsLoading,
    unlinkSuiteId,
    setUnlinkSuiteId,
    unlinkSuiteSelectedTestIds,
    setUnlinkSuiteSelectedTestIds,
    unlinkSuiteSaving,
    setUnlinkSuiteSaving,
    unlinkSuiteFieldError,
    setUnlinkSuiteFieldError,
    unlinkSuiteTestsLoading,
    linkSuiteTests,
    unlinkSuiteTests,
    linkSuiteTestOptions,
    unlinkSuiteTestOptions,
  } = suiteLinkingState;

  const testLinkingState = useFeatureTestLinkingState({ featureId, testOptions });
  const {
    linkTestDialog,
    unlinkTestDialog,
    linkTestForm,
    linkTestErrors,
    unlinkTestForm,
    unlinkTestErrors,
    availableTestOptions,
    unlinkTestOptions,
  } = testLinkingState;

  const invalidateGrids = useInvalidateGrids();
  const invalidateQueries = useInvalidateQueries();
  const { flags: featureFlags } = useResolvedFeatureFlags();
  const deltasEnabled = featureFlags?.deltas ?? true;

  const suiteFilters = buildEqFilter('featureId', featureId);
  const testQueryFn = useMemo(() => {
    if (!feature || featureId.length === 0) {
      return async (input: GridQueryInput): Promise<QueryResult<TestListItemDto>> =>
        Promise.resolve({ items: [], total: 0, page: input.page, pageSize: input.pageSize });
    }
    return async (input: GridQueryInput): Promise<QueryResult<TestListItemDto>> => {
      const filters = mergeFilters(input.filters, buildEqFilter('featureId', featureId));
      return (await queryTests(featureProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        filters,
        include: {
          suites: { ids: true, items: true, primary: true },
          latestExecution: true,
          metrics: true,
          metricDeltas: deltasEnabled,
        },
      })) as QueryResult<TestListItemDto>;
    };
  }, [deltasEnabled, feature, featureId, featureProjectId, selectedOrganisationId]);

  return {
    searchSlug,
    featureId,
    featureProjectId,
    featureQuery,
    feature,
    suitesQuery,
    testsQuery,
    suiteOptions,
    testOptions,
    releasesQuery,
    releaseOptions,
    releaseOptionIds,
    editForm,
    editErrors,
    editValues,
    linkSuiteDialog,
    unlinkSuiteDialog,
    linkTestDialog,
    unlinkTestDialog,
    linkTestForm,
    linkTestErrors,
    unlinkTestForm,
    unlinkTestErrors,
    editOpen,
    setEditOpen,
    deleteOpen,
    setDeleteOpen,
    linkSuiteId,
    setLinkSuiteId,
    linkSuiteSelectedTestIds,
    setLinkSuiteSelectedTestIds,
    linkSuiteSaving,
    setLinkSuiteSaving,
    linkSuiteFieldError,
    setLinkSuiteFieldError,
    linkSuiteTestsLoading,
    unlinkSuiteId,
    setUnlinkSuiteId,
    unlinkSuiteSelectedTestIds,
    setUnlinkSuiteSelectedTestIds,
    unlinkSuiteSaving,
    setUnlinkSuiteSaving,
    unlinkSuiteFieldError,
    setUnlinkSuiteFieldError,
    unlinkSuiteTestsLoading,
    linkSuiteTestOptions,
    unlinkSuiteTestOptions,
    linkSuiteTests,
    unlinkSuiteTests,
    availableTestOptions,
    unlinkTestOptions,
    availableSuiteOptions,
    linkedSuites,
    suiteFilters,
    testQueryFn,
    updateFeature,
    deleteFeature,
    updateSuite,
    updateTest,
    invalidateGrids,
    invalidateQueries,
  };
};
